import React from 'react';
import { Checkbox, Label, Select, RangeSlider } from 'flowbite-react';
import { Shape } from '../Types';

interface SetupControlsProps {
  selectedShape: Shape;
  invertImage: boolean;
  maskSize: number;
  pathLength: number;
  numberOfPins: number;
  onShapeChange: (shape: Shape) => void;
  onInvertChange: (invert: boolean) => void;
  onMaskSizeChange: (size: number) => void;
  onPathLengthChange: (length: number) => void;
  onNumberOfPinsChange: (count: number) => void;
}

const SetupControls: React.FC<SetupControlsProps> = ({ 
  selectedShape, 
  invertImage,
  maskSize,
  pathLength,
  numberOfPins,
  onShapeChange, 
  onInvertChange,
  onMaskSizeChange,
  onPathLengthChange,
  onNumberOfPinsChange
}) => {
  return (
    <>
      <h3 className="text-xl font-semibold mb-4">Shape Selection:</h3>
      <div className="flex justify-center mb-8">
        <div className="w-48">
          <Select
            value={selectedShape}
            onChange={(e) => onShapeChange(e.target.value as Shape)}
          >
            <option value="ellipse">Ellipse Shape</option>
            <option value="rectangle">Rectangle Shape</option>
          </Select>
        </div>
      </div>
      
      <h3 className="text-xl font-semibold mb-4">Image Options:</h3>
      <div className="flex justify-center mb-8">
        <div className="flex items-center gap-2">
          <Checkbox
            id="invert-image"
            checked={invertImage}
            onChange={(e) => onInvertChange(e.target.checked)}
          />
          <Label htmlFor="invert-image">
            Invert Image (Dark becomes Light)
          </Label>
        </div>
      </div>

      <h3 className="text-xl font-semibold mb-4">Edge Mask:</h3>
      <div className="flex justify-center mb-8">
        <div className="w-64">
          <Label htmlFor="mask-size">{`Mask Size: ${maskSize}%`}</Label>
          <RangeSlider
            id="mask-size"
            min={50}
            max={100}
            value={maskSize}
            onChange={(e) => onMaskSizeChange(Number(e.target.value))}
          />
          <p className="text-sm text-gray-500 mt-2">
            Ignore edges outside {maskSize}% of the shape
          </p>
        </div>
      </div>

      <h3 className="text-xl font-semibold mb-4">Algorithm Parameters:</h3>
      <div className="flex flex-col items-center gap-6 mb-8">
        <div className="w-64">
          <Label htmlFor="path-length" className="block mb-2 font-medium">
            Path Length: <span className="text-blue-600 font-semibold">{pathLength}</span> segments
          </Label>
          <RangeSlider
            id="path-length"
            min={100}
            max={2000}
            value={pathLength}
            onChange={(e) => onPathLengthChange(Number(e.target.value))}
          />
          <p className="text-sm text-gray-500 mt-2">
            Number of line segments to draw (100-2000)
          </p>
        </div>
        
        <div className="w-64">
          <Label htmlFor="number-of-pins" className="block mb-2 font-medium">
            Number of Pins: <span className="text-blue-600 font-semibold">{numberOfPins}</span> pins
          </Label>
          <RangeSlider
            id="number-of-pins"
            min={50}
            max={400}
            value={numberOfPins}
            onChange={(e) => onNumberOfPinsChange(Number(e.target.value))}
          />
          <p className="text-sm text-gray-500 mt-2">
            Pins around the perimeter (50-400)
          </p>
        </div>
      </div>
    </>
  );
};

export default SetupControls;